import api from './api';
import { tokenStorage } from './storageService';
import type { LoginDto, RegisterDto, AuthResponse, User, UpdateProfileDto, ChangePasswordDto } from '../types';

export const authService = {
  async login(credentials: LoginDto): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    const { token, refreshToken, user, expiresAt } = response.data;
    tokenStorage.setTokens(token, refreshToken, expiresAt);
    tokenStorage.setUser(user);
    return response.data;
  },

  async register(data: RegisterDto): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    const { token, refreshToken, user, expiresAt } = response.data;
    tokenStorage.setTokens(token, refreshToken, expiresAt);
    tokenStorage.setUser(user);
    return response.data;
  },

  async logout(): Promise<void> {
    try {
      // Try to notify the server first (while token is still in storage)
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore server errors - proceed with local logout anyway
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local storage, even if API call fails
      tokenStorage.clearAll();
    }
  },

  async refreshToken(): Promise<AuthResponse> {
    const currentRefreshToken = tokenStorage.getRefreshToken();
    const currentToken = tokenStorage.getToken();
    if (!currentRefreshToken || !currentToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthResponse>('/auth/refresh-token', {
      token: currentToken,
      refreshToken: currentRefreshToken,
    });
    const { token, refreshToken, expiresAt } = response.data;
    tokenStorage.setTokens(token, refreshToken, expiresAt);
    return response.data;
  },

  getCurrentUser(): User | null {
    return tokenStorage.getUser();
  },

  isAuthenticated(): boolean {
    return tokenStorage.isAuthenticated();
  },

  getToken(): string | null {
    return tokenStorage.getToken();
  },

  getRefreshToken(): string | null {
    return tokenStorage.getRefreshToken();
  },

  isTokenExpired(): boolean {
    return tokenStorage.isTokenExpired();
  },

  clearAuth(): void {
    tokenStorage.clearAll();
  },

  async updateProfile(data: UpdateProfileDto): Promise<User> {
    const response = await api.put<User>('/auth/profile', data);
    // Update stored user data
    tokenStorage.setUser(response.data);
    return response.data;
  },

  async changePassword(data: ChangePasswordDto): Promise<void> {
    await api.post('/auth/change-password', data);
  },

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(data: {
    email: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/reset-password', data);
    return response.data;
  },
};

export default authService;
